import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FeatureFlagConfigEditorKind, featureFlagConfigEditorKind, featureFlagConfigEnumType, } from "healthex-common/features.dtos";
import { enumValues } from "healthex-common/utils";
import { observer } from "mobx-react";
import * as React from "react";
import { addDisplayName } from "../../Infra/display-name";
import { HXAutocomplete, HXAutocompleteMulti } from "../HXAutocomplete";
import { HXCheckbox, HXTextField } from "../base.styles";
import { FeatureConfigTextArea, ScopeCardField, ScopeCardFieldLabel } from "./GlobalAdminFeatureEnablement.styles";
function parseJsonObject(configurationJson) {
    const trimmed = configurationJson.trim();
    if (!trimmed) {
        return {};
    }
    try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
            return { ...parsed };
        }
    }
    catch {
        return null;
    }
    return null;
}
function serializeJsonObject(value) {
    if (Object.keys(value).length === 0) {
        return ``;
    }
    return JSON.stringify(value, null, 2);
}
function parseStringArray(configurationJson) {
    const trimmed = configurationJson.trim();
    if (!trimmed) {
        return [];
    }
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
            return parsed;
        }
    }
    catch {
    }
    return trimmed
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);
}
function serializeStringArray(values) {
    if (values.length === 0) {
        return ``;
    }
    return JSON.stringify(values, null, 2);
}
function readNumberField(value) {
    const trimmed = value.trim();
    if (!trimmed) {
        return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : value;
}
function formatNumberField(value) {
    return typeof value === "number" || typeof value === "string" ? String(value) : ``;
}
function hasUnsupportedNumberField(config, fields) {
    return fields.some((field) => {
        const value = config[field];
        return value !== undefined && typeof value !== "number" && typeof value !== "string";
    });
}
function hasUnsupportedBooleanField(config, fields) {
    return fields.some((field) => {
        const value = config[field];
        return value !== undefined && typeof value !== "boolean";
    });
}
function updateBooleanField(config, field, checked, onChange) {
    const nextConfig = { ...config };
    if (checked) {
        delete nextConfig[field];
    }
    else {
        nextConfig[field] = false;
    }
    onChange(serializeJsonObject(nextConfig));
}
function updateNumberField(config, field, rawValue, onChange) {
    const nextConfig = { ...config };
    const parsed = readNumberField(rawValue);
    if (parsed === undefined) {
        delete nextConfig[field];
    }
    else {
        nextConfig[field] = parsed;
    }
    onChange(serializeJsonObject(nextConfig));
}
const JsonConfigEditor = observer(({ configurationJson, disabled, onChange }) => (_jsxs(ScopeCardField, { children: [_jsx(ScopeCardFieldLabel, { children: "Configuration (JSON)" }), _jsx(FeatureConfigTextArea, { value: configurationJson, disabled: disabled, onChange: (event) => onChange(event.target.value), spellCheck: false })] })));
addDisplayName(JsonConfigEditor, "JsonConfigEditor");
const DateConfigEditor = observer(({ configurationJson, disabled, onChange }) => (_jsxs(ScopeCardField, { children: [_jsx(ScopeCardFieldLabel, { children: "Reset date (ISO 8601)" }), _jsx(HXTextField, { value: configurationJson, disabled: disabled, placeholder: "2025-01-01T00:00:00.000Z", onChange: (event) => onChange(event.target.value), fullWidth: true })] })));
addDisplayName(DateConfigEditor, "DateConfigEditor");
const EnumConfigEditor = observer(({ feature, configurationJson, disabled, onChange }) => {
    const enumType = featureFlagConfigEnumType(feature);
    const configuredValue = configurationJson.trim();
    const validOptions = enumType ? enumValues(enumType) : [];
    const options = configuredValue && !validOptions.includes(configuredValue) ? [...validOptions, configuredValue] : validOptions;
    const selected = configuredValue || null;
    return (_jsx(ScopeCardField, { children: _jsx(HXAutocomplete, { label: "Configuration", options: options, value: selected, disabled: disabled, getId: (value) => value, getDisplayValue: (value) => value, onChange: (value) => onChange(value !== null && value !== void 0 ? value : ``) }) }));
});
addDisplayName(EnumConfigEditor, "EnumConfigEditor");
const EnumArrayConfigEditor = observer(({ feature, configurationJson, disabled, onChange }) => {
    const enumType = featureFlagConfigEnumType(feature);
    const selected = parseStringArray(configurationJson);
    const validOptions = enumType ? enumValues(enumType) : [];
    const options = [...validOptions, ...selected.filter((value) => !validOptions.includes(value))];
    return (_jsx(ScopeCardField, { children: _jsx(HXAutocompleteMulti, { label: "Configuration", options: options, value: selected, disabled: disabled, getId: (value) => value, getDisplayValue: (value) => value, onChange: (values) => onChange(serializeStringArray(values)) }) }));
});
addDisplayName(EnumArrayConfigEditor, "EnumArrayConfigEditor");
function urlLines(text) {
    return text
        .split(`\n`)
        .map((line) => line.trim())
        .filter(Boolean);
}
function sameUrls(left, right) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}
const UrlArrayConfigEditor = observer(({ configurationJson, disabled, onChange }) => {
    const [draft, setDraft] = React.useState(() => ({ text: parseStringArray(configurationJson).join(`\n`), configurationJson }));
    if (draft.configurationJson !== configurationJson) {
        const incomingUrls = parseStringArray(configurationJson);
        setDraft({
            text: sameUrls(incomingUrls, urlLines(draft.text)) ? draft.text : incomingUrls.join(`\n`),
            configurationJson,
        });
    }
    return (_jsxs(ScopeCardField, { children: [_jsx(ScopeCardFieldLabel, { children: "Community auth URLs (one per line)" }), _jsx(FeatureConfigTextArea, { value: draft.text, disabled: disabled, onChange: (event) => {
                    const text = event.target.value;
                    setDraft({ text, configurationJson });
                    onChange(serializeStringArray(urlLines(text)));
                }, spellCheck: false })] }));
});
addDisplayName(UrlArrayConfigEditor, "UrlArrayConfigEditor");
const BACKGROUND_DATA_REFRESH_FIELDS = ["minimumDaysSinceLastRetrieval", "maximumSetSize"];
const BackgroundDataRefreshConfigEditor = observer((props) => {
    const { configurationJson, disabled, onChange } = props;
    const config = parseJsonObject(configurationJson);
    if (config === null || hasUnsupportedNumberField(config, BACKGROUND_DATA_REFRESH_FIELDS)) {
        return _jsx(JsonConfigEditor, { ...props });
    }
    const updateField = (field, rawValue) => updateNumberField(config, field, rawValue, onChange);
    return (_jsxs(_Fragment, { children: [_jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Minimum days since last retrieval", value: formatNumberField(config.minimumDaysSinceLastRetrieval), disabled: disabled, onChange: (event) => updateField("minimumDaysSinceLastRetrieval", event.target.value), fullWidth: true }) }), _jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Maximum set size", value: formatNumberField(config.maximumSetSize), disabled: disabled, onChange: (event) => updateField("maximumSetSize", event.target.value), fullWidth: true }) })] }));
});
addDisplayName(BackgroundDataRefreshConfigEditor, "BackgroundDataRefreshConfigEditor");
const PATIENT_NUDGE_FIELDS = ["delayMinutes", "progressExpirationMinutes", "maxNudgesPerFlow"];
const PatientNudgeNotificationsConfigEditor = observer((props) => {
    const { configurationJson, disabled, onChange } = props;
    const config = parseJsonObject(configurationJson);
    if (config === null || hasUnsupportedNumberField(config, PATIENT_NUDGE_FIELDS)) {
        return _jsx(JsonConfigEditor, { ...props });
    }
    const updateField = (field, rawValue) => updateNumberField(config, field, rawValue, onChange);
    return (_jsxs(_Fragment, { children: [_jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Delay minutes", value: formatNumberField(config.delayMinutes), disabled: disabled, onChange: (event) => updateField("delayMinutes", event.target.value), fullWidth: true }) }), _jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Progress expiration minutes", value: formatNumberField(config.progressExpirationMinutes), disabled: disabled, onChange: (event) => updateField("progressExpirationMinutes", event.target.value), fullWidth: true }) }), _jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Max nudges per flow", value: formatNumberField(config.maxNudgesPerFlow), disabled: disabled, onChange: (event) => updateField("maxNudgesPerFlow", event.target.value), fullWidth: true }) })] }));
});
addDisplayName(PatientNudgeNotificationsConfigEditor, "PatientNudgeNotificationsConfigEditor");
const AI_RECORDS_READY_EMAIL_DELAY_FIELDS = ["delaySeconds"];
const AiRecordsReadyEmailDelayConfigEditor = observer((props) => {
    const { configurationJson, disabled, onChange } = props;
    const config = parseJsonObject(configurationJson);
    if (config === null || hasUnsupportedNumberField(config, AI_RECORDS_READY_EMAIL_DELAY_FIELDS)) {
        return _jsx(JsonConfigEditor, { ...props });
    }
    return (_jsx(ScopeCardField, { children: _jsx(HXTextField, { label: "Delay seconds", value: formatNumberField(config.delaySeconds), disabled: disabled, onChange: (event) => updateNumberField(config, "delaySeconds", event.target.value, onChange), fullWidth: true }) }));
});
addDisplayName(AiRecordsReadyEmailDelayConfigEditor, "AiRecordsReadyEmailDelayConfigEditor");
const RETRIEVAL_STATS_FIELDS = ["recordSummary", "resourceIdStats"];
const RetrievalStatsConfigEditor = observer((props) => {
    const { configurationJson, disabled, onChange } = props;
    const config = parseJsonObject(configurationJson);
    if (config === null || hasUnsupportedBooleanField(config, RETRIEVAL_STATS_FIELDS)) {
        return _jsx(JsonConfigEditor, { ...props });
    }
    return (_jsxs(_Fragment, { children: [_jsx(ScopeCardField, { children: _jsx(HXCheckbox, { checked: config.recordSummary !== false, disabled: disabled, onChange: (_event, checked) => updateBooleanField(config, "recordSummary", checked, onChange), label: "Per-location record summary" }) }), _jsx(ScopeCardField, { children: _jsx(HXCheckbox, { checked: config.resourceIdStats !== false, disabled: disabled, onChange: (_event, checked) => updateBooleanField(config, "resourceIdStats", checked, onChange), label: "FHIR resource-id conformance stats" }) })] }));
});
addDisplayName(RetrievalStatsConfigEditor, "RetrievalStatsConfigEditor");
export function renderFeatureConfigBody(props) {
    switch (featureFlagConfigEditorKind(props.feature)) {
        case FeatureFlagConfigEditorKind.Date:
            return _jsx(DateConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.Enum:
            return _jsx(EnumConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.EnumArray:
            return _jsx(EnumArrayConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.UrlArray:
            return _jsx(UrlArrayConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.BackgroundDataRefresh:
            return _jsx(BackgroundDataRefreshConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.PatientNudgeNotifications:
            return _jsx(PatientNudgeNotificationsConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.AiRecordsReadyEmailDelay:
            return _jsx(AiRecordsReadyEmailDelayConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.RetrievalStats:
            return _jsx(RetrievalStatsConfigEditor, { ...props });
        case FeatureFlagConfigEditorKind.Json:
        default:
            return _jsx(JsonConfigEditor, { ...props });
    }
}
